/**
 * №13.1 (§2.5) — Vendor invoices Place (`/app/billing/vendor-invoices`).
 * A bill FROM a supplier, allocated to matters. Lifecycle affordances
 * Approve · Hold · Mark paid drive `update_vendor_invoice {status}`;
 * `pay_when_client_pays` is shown. No GL write (L-2; Xero at 12.x).
 */
import { useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../../lib/mcp-client";
import { money } from "../../components/ledger/format";
import { showToast } from "../../components/primitives";
import { useEntityType } from "../../../lib/hooks/use-kuvio";
import { useVendorInvoices } from "../../hooks/use-time-billing";
import { CreateEntityModal } from "./CreateEntityModal";
import { TableSubjectHead } from "./TableSubjectHead";

// The next status a lifecycle button advances a vendor invoice to.
const NEXT: Record<string, { to: string; label: string }[]> = {
  draft: [{ to: "approved", label: "Approve" }],
  approved: [{ to: "hold", label: "Hold" }, { to: "paid", label: "Mark paid" }],
  hold: [{ to: "approved", label: "Release" }, { to: "paid", label: "Mark paid" }],
  paid: [],
};

export function VendorInvoicesPage() {
  const navigate = useNavigate();
  const list = useVendorInvoices();
  const type = useEntityType("vendor-invoice");
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: string }) => mcpClient.updateEntity("update_vendor_invoice", v.id, { status: v.status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["entities", "vendor-invoice"] }),
  });

  const rows = list.data ?? [];

  return (
    <div className="screen">
      {/* №14.1.1-B — pagehead + card, like every other Place list (was a
          `.surface__bar` title over a border-stripped card whose footnote
          rendered outside the card edge). */}
      <TableSubjectHead
        title="Vendor invoices"
        sub={<>{rows.length} · supplier bills allocated to matters · held until the client settles when pay-when-client-pays is set</>}
        actions={type.data ? <button className="stagebtn stagebtn--primary" onClick={() => setShowCreate(true)}>＋ Vendor invoice</button> : null}
      />
      <div className="card card--flush">
          {list.isLoading ? <div className="skeleton"><div className="sk-line" style={{ width: "60%" }} /><div className="sk-line" style={{ width: "40%" }} /></div>
            : rows.length === 0 ? <div className="empty-state"><h3>No vendor invoices</h3><p>Add a supplier bill and allocate it to a matter.</p></div>
            : (
              <div className="table-scroll"><table><thead><tr><th>Vendor</th><th>Matter</th><th className="num">Amount</th><th>PWCP</th><th>Status</th></tr></thead><tbody>
                {rows.map((v) => {
                  const status = (v.status as string) ?? "draft";
                  return (
                    <tr key={v.id}>
                      <td className="primary" onClick={() => navigate(`/app/place/vendor-invoice/${v.id}`)} style={{ cursor: "pointer" }}>{(v.vendor as string) ?? "—"}</td>
                      <td>{(v.matter as string) ?? "—"}</td>
                      <td className="num money">{money((v.amount_minor as number) ?? 0)}</td>
                      <td className="muted tiny">{(v.pay_when_client_pays as string) === "yes" ? "yes" : "—"}</td>
                      <td><span className={`pill ${status === "paid" ? "pill--issued" : status === "hold" ? "pill--hold" : status === "approved" ? "pill--approved" : "pill--pending"}`}><span className="dot" />{status}</span>
                        {(NEXT[status] ?? []).map((n) => (
                          <button key={n.to} className="act" style={{ padding: "2px 8px", marginLeft: 6 }} disabled={setStatus.isPending}
                            onClick={async () => { try { await setStatus.mutateAsync({ id: v.id as string, status: n.to }); showToast(`${n.label} — ${v.vendor}`, "success"); } catch (e) { showToast((e as Error).message, "error"); } }}>{n.label}</button>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody></table></div>
            )}
      </div>
      {type.data && <CreateEntityModal entityType={type.data} open={showCreate} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); void list.refetch(); }} />}
    </div>
  );
}
